import React, { useState, useRef, useEffect, useMemo } from 'react';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import { motion, AnimatePresence } from 'framer-motion';
import { CustomDatePicker, CustomSelect, CustomNumberInput } from '../../shared/components/custom';
import {
  History,
  Search,
  Calendar,
  ChevronDown,
  ChevronUp,
  Filter,
  Download,
  Upload,
  FileText,
  FileSpreadsheet,
  FileOutput,
  List,
  Grid,
  RefreshCcw,
  TrendingUp,
  TrendingDown,
  Banknote,
  Activity,
  X,
  LayoutList,
  Clock,
  LayoutGrid,
  Shield,
  Layers,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import '../../shared/styles/GPSTrackingDatePicker.css';
import { useTranslation } from 'react-i18next';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import { buildCaisseColumnDefs, useCaisseColumnVisibility, caisseTableColSpan, sliceCaisseListItems, CAISSE_FORM_GRID } from './caisseListPageUtils';
import { useTreasuryConfig, useTreasuryListViewMode } from './useTreasuryConfig';
import { QawaemTableStickyActionsTd } from '../../products/saudi/components/QawaemTableStickyActions';
import localApiAdapter from '../../core/services/localApiAdapter';
import { mapMovementHistoryFromApi } from './treasuryApiMappers';

const MovementsHistoryPage = () => {
  const { t, i18n } = useTranslation();
  const treasuryConfig = useTreasuryConfig();
  const [viewMode, setViewMode] = useTreasuryListViewMode(treasuryConfig);
  const p = (key, opts) => t(`modules.caisse.pages.movementsHistoryPage.${key}`, opts);
  const cfgTheme = useModuleConfigTheme();
  const isLight = cfgTheme.light;
  const dateLocale = getBrandLocale(i18n.language);
  const formatMoney = (amount) => formatBrandMoney(amount);
  const formatMovementAmount = (movement) => `${movement.type === 'entree' ? '+' : '-'}${formatBrandMoney(movement.montant, false)}`;
  const formatAmount = (amount) => formatBrandMoney(amount);

  // ===== STATE MANAGEMENT =====
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedMovement, setSelectedMovement] = useState(null);
  
  const [currentView, setCurrentView] = useState('all');
  const [quickFilter, setQuickFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const { filters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  const columnFilterRules = useMemo(() => [
    textColumnRule('date', (row) => row.date || ''),
    textColumnRule('reference', (row) => row.reference || ''),
    exactColumnRule('type', (row) => row.type || ''),
    textColumnRule('montant', (row) => String(row.montant ?? '')),
    textColumnRule('solde', (row) => String(row.solde ?? '')),
  ], []);
  
  const [movements, setMovements] = useState([]);
  const [loadingMovements, setLoadingMovements] = useState(false);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadMovements();
  }, []);

  const loadMovements = async () => {
    setLoadingMovements(true);
    try {
      const data = await localApiAdapter.get('/treasury/history', { limit: 500 });
      setMovements((Array.isArray(data) ? data : []).map(mapMovementHistoryFromApi));
    } catch (error) {
      console.error('Error loading movements history:', error);
      setMovements([]);
    } finally {
      setLoadingMovements(false);
    }
  };

  const handleView = (movement) => {
    setSelectedMovement(movement);
    setShowViewModal(true);
  };

  const clickableStatusOptions = useMemo(() => ([
    { value: 'pending', label: t('modules.caisse.pages.caisseReconciliationPage.status_pending'), icon: Clock, style: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
    { value: 'validated', label: t('modules.caisse.pages.caisseReconciliationPage.status_validated'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
    { value: 'cancelled', label: t('modules.caisse.pages.fundsTransferPage.status_cancelled'), icon: XCircle, style: 'bg-red-500/20 text-red-300 border-red-500/30' },
  ]), [t]);

  const movementsColumnKeys = useMemo(
    () => ['date', 'reference', 'type', 'description', 'montant', 'solde', 'register', 'status', 'actions'],
    [],
  );
  const { columnVisibility, handleToggleColumn, isColumnVisible } = useCaisseColumnVisibility(movementsColumnKeys);
  const qawaemColumns = useMemo(
    () => buildCaisseColumnDefs([
      { key: 'date', labelKey: 'col_date' },
      { key: 'reference', labelKey: 'col_reference' },
      { key: 'type', labelKey: 'col_type' },
      { key: 'description', labelKey: 'col_description' },
      { key: 'montant', labelKey: 'col_amount' },
      { key: 'solde', labelKey: 'col_balance' },
      { key: 'register', labelKey: 'col_register' },
      { key: 'status', labelKey: 'col_status' },
      { key: 'actions', labelKey: 'col_actions', required: true },
    ], p, columnVisibility),
    [p, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const tableColSpan = caisseTableColSpan(qawaemColumns);

  const stats = {
    total: movements.length,
    entrees: movements.filter(m => m.type === 'entree').length,
    sorties: movements.filter(m => m.type === 'sortie').length,
    soldeActuel: movements.length > 0 ? movements[movements.length - 1].solde : 0
  };

  const filteredMovements = applyColumnFilters(
    movements.filter(movement => {
      if (quickFilter !== 'all') {
        if (quickFilter === 'entree' && movement.type !== 'entree') return false;
        if (quickFilter === 'sortie' && movement.type !== 'sortie') return false;
      }

      const matchesView = currentView === 'all' || currentView === 'par_type' || currentView === 'par_caisse' || movement.type === currentView;

      const matchesSearch = !searchTerm ||
        movement.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        movement.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        movement.caisse?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        movement.utilisateur?.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesView && matchesSearch;
    }),
    filters,
    columnFilterRules,
  );
  const pagedMovements = useMemo(
    () => sliceCaisseListItems(filteredMovements, treasuryConfig.itemsPerPage),
    [filteredMovements, treasuryConfig.itemsPerPage],
  );

  const {
    selectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(filteredMovements);

  const getTypeBadge = (type) => {
    const styles = {
      'entree': 'bg-green-500/20 text-green-300 border-green-500/30',
      'sortie': 'bg-red-500/20 text-red-300 border-red-500/30',
      'transfert': 'bg-blue-500/20 text-blue-300 border-blue-500/30'
    };
    const label = p(`type_${type}`);
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${styles[type] || styles['transfert']}`}>
        {label}
      </span>
    );
  };

  const formatDate = (date) => new Date(date).toLocaleDateString(dateLocale);
  const formatTime = (date) => new Date(date).toLocaleTimeString(dateLocale);
  const formatDateTime = (date) => new Date(date).toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric' });
  const formatTimeShort = (date) => new Date(date).toLocaleTimeString(dateLocale, { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="space-y-6 clients-container">
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white">
            {p('title')}
            <button onClick={() => window.location.reload()} className="refresh-button ml-4" title={p('refresh')}>
              <RefreshCcw size={20} />
            </button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${
              showAdvancedOptions 
                ? 'bg-gradient-to-r from-purple-500 to-purple-600 border-purple-400' 
                : 'bg-white/5 border-white/20'
            } border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
            title={p('advanced_options')}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{p('advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Stats Cards - GPS Glow Effect (EXACT COPY from FacturiersPage lines 502-523) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: p('stat_total_movements'), value: stats.total, icon: History, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_entries'), value: stats.entrees, icon: TrendingUp, color: 'from-green-500 to-green-600' },
          { title: p('stat_exits'), value: stats.sorties, icon: TrendingDown, color: 'from-red-500 to-red-600' },
          { title: p('stat_current_balance'), value: formatAmount(stats.soldeActuel), icon: Banknote, color: 'from-purple-500 to-purple-600' },
          { title: p('stat_activity'), value: p('stat_activity_ops', { count: stats.entrees + stats.sorties }), icon: Activity, color: 'from-emerald-500 to-emerald-600' }
        ].slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div><p className="text-white/60 text-sm font-medium">{stat.title}</p><p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p></div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Advanced Options - GPS Pattern with Main Tabs & Distribution */}
      <AnimatePresence>
        {showAdvancedOptions && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
            <div className="space-y-4 pt-4">
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
                <div className="flex space-x-1 overflow-x-auto table-scroll">
                  {[
                    { id: 'all', nameKey: 'tab_general', icon: LayoutGrid },
                    { id: 'par_type', nameKey: 'tab_by_type', icon: Shield },
                    { id: 'par_caisse', nameKey: 'tab_by_register', icon: Layers }
                  ].map((tab) => (
                    <button key={tab.id} onClick={() => { setCurrentView(tab.id === 'par_type' ? 'all' : tab.id); setQuickFilter('all'); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${currentView === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                      <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{p(tab.nameKey)}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
                <div className="flex space-x-1 overflow-x-auto table-scroll">
                  {[
                    { id: 'all', nameKey: 'filter_all', icon: Layers },
                    { id: 'entree', nameKey: 'filter_entries', icon: TrendingUp },
                    { id: 'sortie', nameKey: 'filter_exits', icon: TrendingDown }
                  ].map((filter) => (
                    <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                      <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{p(filter.nameKey)}</span>
                    </button>
                  ))}
                </div>
              </div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">{p('distribution_title')}</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { nameKey: 'dist_entries', count: stats.entrees, color: "bg-gradient-to-r from-green-500 to-green-600" },
                    { nameKey: 'dist_exits', count: stats.sorties, color: "bg-gradient-to-r from-red-500 to-red-600" },
                    { nameKey: 'dist_transfers', count: movements.filter(m => m.type === 'transfert').length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
                    { nameKey: 'dist_total', count: stats.total, color: "bg-gradient-to-r from-purple-500 to-purple-600" }
                  ].map((item) => (
                    <button key={item.nameKey} className="bg-white/5 rounded-xl p-4 border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all cursor-pointer text-left">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-medium text-white/80">{p(item.nameKey)}</h4>
                        <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                      </div>
                      <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                        <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                          const total = stats.total;
                          return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                        })()}%` }}></div>
                      </div>
                      <p className="mt-2 text-sm text-white/60">{p('percent_of_total', { percent: (() => {
                        const total = stats.total;
                        return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                      })() })}</p>
                    </button>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top Controls Bar - EXACT GPS Pattern (FacturiersPage lines 581-869) */}
      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="overflow-x-auto table-horizontal-scroll">
            <table className="w-full clients-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                    selectAllTitle={t('common.select_all', { defaultValue: 'تحديد الكل' })}
                    deselectAllTitle={t('common.deselect_all', { defaultValue: 'إلغاء التحديد' })}
                  />
                  {isColumnVisible('date') ? (
                    <TableColumnHeaderFilter
                      columnKey="date"
                      label={p('col_date')}
                      value={filters.date || ''}
                      onChange={(v) => setFilter('date', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      thClassName="text-white font-semibold"
                    />
                  ) : null}
                  {isColumnVisible('reference') ? (
                    <TableColumnHeaderFilter
                      columnKey="reference"
                      label={p('col_reference')}
                      value={filters.reference || ''}
                      onChange={(v) => setFilter('reference', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      thClassName="text-white font-semibold"
                    />
                  ) : null}
                  {isColumnVisible('type') ? (
                    <TableColumnHeaderFilter
                      columnKey="type"
                      label={p('col_type')}
                      kind="select"
                      value={filters.type || ''}
                      onChange={(v) => setFilter('type', v)}
                      options={[
                        { value: 'entree', label: p('type_entree') },
                        { value: 'sortie', label: p('type_sortie') },
                        { value: 'transfert', label: p('type_transfert') },
                      ]}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      thClassName="text-white font-semibold"
                    />
                  ) : null}
                  {isColumnVisible('description') ? (
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('col_description')}</th>
                  ) : null}
                  {isColumnVisible('montant') ? (
                    <TableColumnHeaderFilter
                      columnKey="montant"
                      label={p('col_amount')}
                      value={filters.montant || ''}
                      onChange={(v) => setFilter('montant', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      thClassName="text-white font-semibold"
                    />
                  ) : null}
                  {isColumnVisible('solde') ? (
                    <TableColumnHeaderFilter
                      columnKey="solde"
                      label={p('col_balance')}
                      value={filters.solde || ''}
                      onChange={(v) => setFilter('solde', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      thClassName="text-white font-semibold"
                    />
                  ) : null}
                  {isColumnVisible('register') ? (
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('col_register')}</th>
                  ) : null}
                  {isColumnVisible('status') ? (
                    <th className="px-6 py-4 text-center text-white font-semibold">{t('common.status')}</th>
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                    label={p('col_actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumns.length}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredMovements.length > 0 ? (
                  pagedMovements.map((movement, index) => (
                    <tr key={movement.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(movement.id) ? 'bg-white/10' : ''}`}>
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(movement.id)}
                        onChange={() => handleSelectRecord(movement.id)}
                      />
                      {isColumnVisible('date') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1.5 text-white font-semibold">
                              <Clock size={14} className="text-white/50" />
                              {formatDateTime(movement.date)}
                            </div>
                            <span className="text-white/50 text-xs mt-0.5">
                              {formatTimeShort(movement.date)}
                            </span>
                          </div>
                        </td>
                      ) : null}
                      {isColumnVisible('reference') ? (
                        <td className="px-6 py-4"><span className="text-white font-semibold">{movement.reference}</span></td>
                      ) : null}
                      {isColumnVisible('type') ? (
                        <td className="px-6 py-4">{getTypeBadge(movement.type)}</td>
                      ) : null}
                      {isColumnVisible('description') ? (
                        <td className="px-6 py-4"><span className="text-white/80">{movement.description}</span></td>
                      ) : null}
                      {isColumnVisible('montant') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className={`font-bold ${movement.type === 'entree' ? 'text-green-400' : 'text-red-400'}`}>
                              {formatMovementAmount(movement)}
                            </span>
                          </div>
                        </td>
                      ) : null}
                      {isColumnVisible('solde') ? (
                        <td className="px-6 py-4"><span className="text-white font-bold">{formatAmount(movement.solde)}</span></td>
                      ) : null}
                      {isColumnVisible('register') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{movement.caisse}</span>
                            <span className="text-white/50 text-xs mt-0.5">{movement.utilisateur}</span>
                          </div>
                        </td>
                      ) : null}
                      {isColumnVisible('status') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={movement.status || 'validated'}
                              recordId={movement.id}
                              options={clickableStatusOptions}
                              title={p('click_change_status')}
                            />
                          </div>
                        </td>
                      ) : null}
                      <QawaemTableStickyActionsTd>
                        <QawaemErpRowActions
                          onView={() => handleView(movement)}
                          viewTitle={p('action_view')}
                        />
                      </QawaemTableStickyActionsTd>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><History size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty_no_movements')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* View Modal */}
      {showViewModal && selectedMovement ? (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={() => { setShowViewModal(false); setSelectedMovement(null); }}
          title={p('modal_title')}
          icon={History}
          maxWidth="1100px"
        >
              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><History size={18} className="text-emerald-400" />{p('section_transaction_info')}</h3>
                  <div className={CAISSE_FORM_GRID}>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_reference')}</p><p className="text-white font-medium">{selectedMovement.reference}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_type')}</p>{getTypeBadge(selectedMovement.type)}</div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_date')}</p><p className="text-white font-medium">{formatDate(selectedMovement.date)}</p></div>
                    <div className="md:col-span-3"><p className="text-white/60 text-sm mb-1">{p('label_description')}</p><p className="text-white font-medium">{selectedMovement.description}</p></div>
                  </div>
                </div>
                <div className={CAISSE_FORM_GRID}>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4">{p('section_amount_details')}</h3>
                    <div className="space-y-3">
                      <div><p className="text-white/60 text-sm mb-1">{p('label_amount')}</p><p className={`font-bold text-xl ${selectedMovement.type === 'entree' ? 'text-green-400' : 'text-red-400'}`}>{formatMovementAmount(selectedMovement)}</p></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('label_balance_after_operation')}</p><p className="text-white font-medium">{formatAmount(selectedMovement.solde)}</p></div>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4">{p('section_register_info')}</h3>
                    <div className="space-y-3">
                      <div><p className="text-white/60 text-sm mb-1">{p('label_register')}</p><p className="text-white font-medium">{selectedMovement.caisse}</p></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('label_user')}</p><p className="text-white font-medium">{selectedMovement.utilisateur}</p></div>
                    </div>
                  </div>
                </div>
              </div>
        </QawaemErpViewModal>
      ) : null}
    </div>
  );
};

export default MovementsHistoryPage;


